import type { Attribute } from '@strapi/strapi';
import { useEffect, useState } from 'react';

import type { Audience, LocalePrefix } from '@tmh-website/shared/const';
import type { LinkField } from '@tmh-website/shared/cms';

/**
 * Turns home links into actually home links (root url).
 *
 * @param url - The URL (as string) to modify.
 * @param [trimTrailingSlash=false] - Whether the trailing slash should be removed.
 */
export function cleanLink(url: string, trimTrailingSlash: boolean = false) {
  const urlWithParameters = url.replace(/\/home\?/, '?');
  const replacedUrl = urlWithParameters.replace(/\/home$/, trimTrailingSlash ? '' : '/');
  return replacedUrl;
}

export function getHomepagelink(
  homepageLinks: Attribute.GetValues<'navigation.homepage-link-entry'>[],
  locale: LocalePrefix,
  audience: Audience
) {
  let newHomepageLink = `/${locale}/`;

  const targetEntry = homepageLinks?.find(entry => entry.audience?.key === audience);
  if ((targetEntry?.page as LinkField)?.value) {
    newHomepageLink = cleanLink(`/${locale}${(targetEntry?.page as LinkField)?.value}`);
  }

  return newHomepageLink;
}

/**
 * Hides links from the DOM for SEO reasons.
 */
export function useMaskLink(enabled: boolean, href: string | undefined) {
  const [renderedHref, setRenderedHref] = useState(enabled ? undefined : href);

  useEffect(() => {
    if (!enabled && href !== undefined && href !== renderedHref) {
      setRenderedHref(href);
    }
  }, [enabled, href]);

  return {
    renderedHref,
    onMouseEnter: enabled ? () => setRenderedHref(href) : undefined,
    onMouseLeave: enabled ? () => setRenderedHref(undefined) : undefined,
  };
}

enum VideoType {
  YouTube = 'YouTube',
  Vimeo = 'Vimeo',
}

export interface VideoEmbedData {
  id: string;
  type: VideoType;
  thumbnailUrl: string;
  /**
   * Properties of the iframe element
   */
  iFrame: {
    allow: string;
    title: string;
    src: string;
  };
}

export const videoIdPatterns: Array<{ pattern: RegExp; type: VideoType }> = [
  {
    // Any Vimeo link
    type: VideoType.Vimeo,
    pattern: /vimeo\.com(?:.*)(?:\/)(\d+)/,
  },
  {
    // Any YouTube link
    type: VideoType.YouTube,
    pattern: /(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]+).*/,
  },
];

/**
 * Turns a video URL into an VideoUrlData object. Returns false if the url is ivalid.
 * @param url
 * @returns
 */
export function getVideoEmbedData(url: string | undefined): VideoEmbedData | false {
  if (!url) {
    return false;
  }
  const trimmedUrl = url.trim();
  for (const { pattern, type } of videoIdPatterns) {
    const id = trimmedUrl.match(pattern)?.[1];
    if (id) {
      return {
        id,
        type,
        thumbnailUrl:
          type === VideoType.YouTube ? `https://img.youtube.com/vi/${id}/0.jpg` : `https://vumbnail.com/${id}.jpg`,
        iFrame: {
          allow:
            type === VideoType.YouTube
              ? 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              : 'autoplay; fullscreen; picture-in-picture',
          title: `${type} video player`,
          src:
            type === VideoType.YouTube
              ? `https://www.youtube-nocookie.com/embed/${id}`
              : `https://player.vimeo.com/video/${id}?dnt=1`,
        },
      };
    }
  }
  return false;
}
